import {
  Input,
  inject,
  OnInit,
  Output,
  signal,
  Component,
  DestroyRef,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { LoggerService } from '@services/logger.service';
import { FormsService } from '../../services/forms.service';

import {
  FormMode,
  FormProperty,
  FormSaveResult,
} from '../../models/form.models';

import { resetForm, submitForm } from '../../actions/form.actions';

import { AppState } from 'src/app/reducers';

import {
  selectFormEntityError,
  selectFormEntityObject,
  selectFormEntitySaving,
  selectFormEntitySuccess,
  selectFormEntityIsCreated,
} from '../../reducers';

import {
  tap,
  filter,
  Subject,
  Observable,
  withLatestFrom,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';
import { SearchableComponent } from 'src/app/shared/utils';

// @TODO refactor this comonent to FormComponent, this is not used in popups only
@Component({
  selector: 'app-general-dynamic-form',
  templateUrl: './general-dynamic-form.component.html',
  styleUrls: ['./general-dynamic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GeneralDynamicFormComponent
  implements OnInit, SearchableComponent
{
  @Input() searchString$: BehaviorSubject<string>;

  @Input() fieldFilterString: string = '';
  @Input() scrollableColumns: boolean = false;
  @Input() isFilterEnabled: boolean = true;
  @Output() formGroupChange = new EventEmitter<FormGroup>();

  public saving$: Observable<boolean>;
  public success$: Observable<boolean>;
  public error$: Observable<string>;
  public object$: Observable<any>;
  public isCreated$: Observable<boolean>;

  public formGroup: FormGroup;
  formMode: FormMode = FormMode.Create;
  // Entity name for the Form Editor (used for state management in ngrx form state)
  public editorFormEntityName = null;
  public formProperties: FormProperty[] = [];
  public activeFormProperties: FormProperty[] = [];

  protected searchString: string;
  protected aggregatedFormId: string = null;

  // True while an opt-in Angular Query save (see saveViaMutation) is in flight.
  protected mutationSaving = signal(false);

  // Set on teardown; guards the mutation-save handlers from firing after the form
  // (or its host dialog) has closed.
  protected isDestroyed = false;

  protected formPropertiesSubject: Subject<void> = new Subject();

  protected destroyRef = inject(DestroyRef);

  protected cdRef = inject(ChangeDetectorRef);

  constructor(
    protected formService: FormsService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {}

  /**
   * Initializes the component after Angular has initialized all data-bound properties.
   * This method is called once after the first `ngOnChanges` method is called.
   */
  ngOnInit() {
    this.destroyRef.onDestroy(() => (this.isDestroyed = true));

    // Filter form properties based on the current form mode
    this.filterFormPropertiesByFormMode();
    // setup ngrx form state management
    this.setupNgrxForm();

    if (this.formGroup === null || this.formGroup === undefined) {
      this.createFormGroup();
    }

    if (this.searchString$) {
      this.searchString$
        .pipe(
          tap((val) => {
            this.searchString = val;
            this.cdRef.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.activeFormProperties = this.formProperties;

    this.formGroupChange.emit(this.formGroup);

    this.watchSaving();
  }

  private filterFormPropertiesByFormMode() {
    this.formProperties = this.formProperties.filter(
      (property) =>
        !property.availableInFormModes ||
        property.availableInFormModes.includes(this.formMode),
    );
  }

  setupNgrxForm() {
    if (this.editorFormEntityName !== null) {
      this.store.dispatch(resetForm({ entity: this.editorFormEntityName }));

      this.saving$ = this.store.select(
        selectFormEntitySaving(this.editorFormEntityName),
      );
      this.success$ = this.store.select(
        selectFormEntitySuccess(this.editorFormEntityName),
      );
      this.error$ = this.store.select(
        selectFormEntityError(this.editorFormEntityName),
      );

      this.object$ = this.store.select(
        selectFormEntityObject(this.editorFormEntityName),
      );
      this.isCreated$ = this.store.select(
        selectFormEntityIsCreated(this.editorFormEntityName),
      );

      // * Trigger toast notifications based on form submission results
      this.success$
        .pipe(
          filter(Boolean),
          withLatestFrom(this.object$, this.isCreated$),
          tap(([_, object, isCreated]) =>
            this.handleFormSuccess(object, isCreated),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();

      this.error$
        .pipe(
          tap((error) => {
            if (error) {
              // TODO: implement or remove
              this.logger.error(
                'GeneralDynamicFormComponent: Form submission error:',
                error,
              );
              this.onSubmitError(error);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private handleFormSuccess(object: any, isCreated: boolean): void {
    if (isCreated) {
      this.onObjectCreated(object);
    } else {
      this.onObjectUpdated(object);
    }
  }

  createFormGroup(): void {
    this.formGroup = this.formService.createFormGroup(
      this.formProperties,
      this.formPropertiesSubject,
    );

    this.formGroup.valueChanges
      .pipe(
        tap(() => {
          this.formGroupChange.emit(this.formGroup);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  submit() {
    this.formService.markAllAsTouchedAndFocusFirstInvalid(this.formGroup);

    // Check if the form is valid
    if (this.formGroup.invalid) {
      this.logger.error(
        'Form is invalid. Please correct the errors and try again.',
      );
      this.logger.debug(this.formGroup.errors);
      this.logger.debug(this.formGroup);
      return;
    }

    // Opt-in Angular Query path (when a subclass overrides saveViaMutation)
    const mutationSave = this.saveViaMutation(this.formGroup.getRawValue());
    if (mutationSave) {
      this.mutationSaving.set(true);
      mutationSave
        .then((result) => {
          if (!this.isDestroyed) {
            this.handleFormSuccess(result.object, result.isCreated);
          }
        })
        .catch((error) => {
          if (!this.isDestroyed) {
            this.onSubmitError(error);
          }
        })
        .finally(() => {
          if (!this.isDestroyed) {
            this.mutationSaving.set(false);
            this.cdRef.detectChanges();
          }
        });
      return;
    }

    // NgRx form-state path
    this.store.dispatch(
      submitForm({
        entity: this.editorFormEntityName,
        formData: this.formGroup.getRawValue(),
      }),
    );
    // Proceed with form submission
    this.onSubmitSuccess(this.formGroup.getRawValue());
  }

  /**
   * Opt-in Angular Query save path, symmetric with the tab-editor base. An editor
   * overrides this to run its mutation and resolve with the saved object; the base
   * then runs onObjectCreated/Updated (or onSubmitError), bypassing the NgRx
   * submitForm mechanism. Returning null keeps the NgRx path.
   */
  protected saveViaMutation(formData: any): Promise<FormSaveResult> | null {
    return null;
  }

  onSubmitSuccess(formValue: any): void {
    // Handle the form submission success
    this.logger.info(
      'GeneralDynamicFormComponent: Form submitted successfully with value:',
      formValue,
    );
  }

  onSubmitError(error: any): void {
    // Handle the form submission error
    this.logger.error(
      'GeneralDynamicFormComponent: Form submission failed with error:',
      error,
    );
  }

  onObjectCreated(object: any): void {
    // Handle the object created event here
    this.logger.info(
      'GeneralDynamicFormComponent: Object created successfully:',
      object,
    );
  }

  onObjectUpdated(object: any): void {
    // Handle the object updated event here
    this.logger.info(
      'GeneralDynamicFormComponent: Object updated successfully:',
      object,
    );
  }

  getFormValue(): any {
    return this.formGroup.value;
  }

  protected watchSaving(): void {
    if (this.saving$) {
      this.saving$
        .pipe(
          // only run change detection when the saving flag actually toggles
          distinctUntilChanged(),
          // side-effect: trigger Angular change detection
          tap(() => this.cdRef.detectChanges()),
          // auto-unsubscribe when the component is destroyed
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }
}
